import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { WithBoundArgs } from '@glint/template';

import type { Service } from 'webapp-types';
import {
	PopupEvent,
	ReportFragmentAction,
	type NewReportSlideFragmentLayout,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { checkToApplyWatchHack, getOrderFromPosition, isAlive, uniq } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import {
	getPositionPropertyFromOrderProperty,
	type OrderProperty,
	type OrdersData,
	type ReportFragmentActionAddData,
	type ReportFragmentActionData,
	type ReportFragmentActionRemoveData,
} from 'webapp/components/reports/editor-base';
import ReportsFragmentGridAddRowComponent from 'webapp/components/reports/fragment-grid/add-row';
import ReportsFragmentGridItemComponent from 'webapp/components/reports/fragment-grid/item';

import NoRerenderSortable from 'webapp/models-custom/no-rerender-sortable';
import type Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

import type { CreateFragmentResponseToGrid } from 'webapp/services/model-manager/report-slide-fragments';

export type SettingsSizeData = {
	id: string;
	settings: {
		width?: number;
		height?: number;
	};
}[];

interface Args {
	model: ReportSlideFragment[];
	report: Report;
	orderProperty: OrderProperty;
	isEditable?: boolean;
	reportSlide?: ReportSlide;
	onFragmentChange?: (
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	) => Promise<unknown>;
}

interface ReportsFragmentGridComponentInterface<T> {
	Args: T;
	Blocks: {
		default: [
			fragments: ReportSlideFragment[],
			Item: WithBoundArgs<
				typeof ReportsFragmentGridItemComponent,
				| 'isPageless'
				| 'isEditable'
				| 'orderProperty'
				| 'slideBackgroundColor'
				| 'removeFragment'
				| 'addFragmentInPosition'
				| 'updateFragmentSize'
				| 'addRow'
			>,
			onFragmentChange: (
				fragment: ReportSlideFragment,
				action: ReportFragmentAction,
				data?: ReportFragmentActionData,
			) => Promise<unknown>,
		];
	};
}

const SORTING_PLACEHOLDER_WIDTH = '1.18em';

export default class ReportsFragmentGridComponent extends Component<
	ReportsFragmentGridComponentInterface<Args>
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;

	private originalBoardLimit!: number;

	node: HTMLElement | null = null;

	resizeObserver: ResizeObserver | null = null;

	noRerenderSortable!: NoRerenderSortable<ReportSlideFragment>;

	itemComponent = ReportsFragmentGridItemComponent;

	reportSlideFragmentsHash = 0; // used for onModelChange, to prevent unnecessary recalculations

	affectedRows: number[] = []; // used for updateGridItemColumnSpan, to prevent unnecessary recalculations

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// temporary higher limit for pageless view
		this.originalBoardLimit = this.mmBoardsService.maxBoardIdsToRemember;
		if (this.isPageless) {
			this.mmBoardsService.maxBoardIdsToRemember = 40;
		}

		this.reportSlideFragmentsHash = this.getReportSlideFragmentsHash();

		this.noRerenderSortable = new NoRerenderSortable<ReportSlideFragment>({
			elementSelector: '.grid-item',
			dataIdProperty: 'id',
			dataSortProperty: this.args.orderProperty,
			getModel: () => {
				return this.args.model;
			},
			onNodeDelete: (el) => {
				el.remove();
			},
		});
	}

	get model() {
		return this.args.model;
	}

	get isPageless() {
		return this.args.report.isPageless; // different handling of board fragments
	}

	get showAddRow() {
		return this.args.isEditable && !(!this.isPageless && this.args.model[0]?.isTypeBoard);
	}

	get positionProperty() {
		return getPositionPropertyFromOrderProperty(this.args.orderProperty);
	}

	get slideBackgroundColor(): string | null {
		return this.args.reportSlide?.color || null;
	}

	@cached
	get lastRowIndex() {
		const positionProperty = this.positionProperty;
		return Math.max(...this.model.map((fragment) => fragment[positionProperty].row), 0);
	}

	get showAddRowPlaceholder(): boolean {
		return (
			!this.args.model.some((fragment) => fragment.isTypeStoryText) &&
			this.args.report.isPageless
		);
	}

	@action
	removeFragment(fragment: ReportSlideFragment) {
		return new Promise<void>((resolve, reject) => {
			let wasSubmitted = false;
			const affirmativeFunc = () => {
				wasSubmitted = true;

				this.affectedRows.push(fragment[this.positionProperty].row);

				const removeData: ReportFragmentActionRemoveData = {
					rowIndex: fragment[this.positionProperty].row,
					columnIndex: fragment[this.positionProperty].column,
				};

				const slide = fragment.slide;
				if (slide?.isTypeCoverPageless) {
					const imageFragment = slide.imageFragments.find(
						(f) => f.order === getOrderFromPosition(1, 1),
					);

					// deleting main pageless header? also remove 11 image
					if (imageFragment && getOrderFromPosition(2, 1) === fragment.order) {
						void this.onFragmentChange(imageFragment, ReportFragmentAction.DELETE, {
							rowIndex: 1,
							columnIndex: 1,
						});
					}
				}

				// then remove the original fragment
				this.onFragmentChange(fragment, ReportFragmentAction.DELETE, removeData)
					.then(() => {
						this.updateGridItemColumnSpan();
						this.updateStretchableFragmentHeight();
						resolve();
					})
					.catch(reject);
			};

			const popup = this.popupService.openConfirmPopup({
				onConfirm: affirmativeFunc,
				title: 'Are you sure you want to delete this block?',
				text: '<strong>This cannot be undone.</strong>',
				confirmAction: 'Delete',
				cancelAction: 'Cancel',
			});

			popup.on(PopupEvent.CLOSING, this, () => {
				if (!wasSubmitted) {
					reject();
				}
			});
		});
	}

	@action
	onFragmentChange(
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	) {
		const onFragmentChange = this.args.onFragmentChange;
		if (onFragmentChange) {
			if ([ReportFragmentAction.CONTENT, ReportFragmentAction.RESIZE].includes(action)) {
				this.updateStretchableFragmentHeight();
			}
			return onFragmentChange(fragment, action, data);
		} else {
			// viewer
			return Promise.resolve();
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		this.noRerenderSortable.ct = $(node);
		this.setupSortable(node);
		this.updateGridItemColumnSpan();

		this.resizeObserver = this.setupResizeObserver(node);

		this.eventManagerService.on(AppEvent.REPORT_FRAGMENT_DELETE, this, (eventData) => {
			void this.removeFragment(eventData.fragment);
		});
	}

	@action
	teardownView(node: HTMLElement): void {
		this.destroySortable(node);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		this.eventManagerService.off(AppEvent.REPORT_FRAGMENT_DELETE, this);

		// reset board limit to original value
		this.mmBoardsService.maxBoardIdsToRemember = this.originalBoardLimit;
	}

	// ------ START NO-RERENDER LOGIC -------

	private getReportSlideFragmentsHash() {
		const reportSlideFragmentsString = this.args.model.reduce(
			(acc, item) => (item.id ? (acc ? acc + ',' : '') + item.id : acc),
			'',
		);

		return hashCode(reportSlideFragmentsString);
	}

	@action
	onModelChange() {
		const newReportSlideFragmentsHash = this.getReportSlideFragmentsHash();
		if (this.reportSlideFragmentsHash === newReportSlideFragmentsHash) {
			return;
		}

		this.reportSlideFragmentsHash = newReportSlideFragmentsHash;

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}

		if (this.node) {
			this.resizeObserver = this.setupResizeObserver(this.node);
		}

		this.noRerenderSortable?.updateModel();

		next(() => {
			if (this.node) {
				this.setupSortable(this.node);
				this.updateGridItemColumnSpan();
				this.updateStretchableFragmentHeight();
			}
		});
	}

	@action
	checkModelOrder(): void {
		schedule('afterRender', () => {
			// this.noRerenderSortable.checkModelNodeOrder();
		});
	}

	get reportSlideFragmentsOrder() {
		return this.model.map((item) => item[this.args.orderProperty]);
	}

	private setupResizeObserver(node: HTMLElement): ResizeObserver | null {
		// Use element which is not changing size when manipulating grid items (scroll also triggers resize observer). Should only trigger on window resize or opening/closing panels.
		const elementToObserve =
			node.closest('.content-inner') || node.closest('.pageless-wrapper');
		if (!elementToObserve) {
			return null;
		}

		let previousWidth = (elementToObserve as HTMLElement).clientWidth;
		const resizeObserver = new ResizeObserver((entries) => {
			const newWidth = entries[0]!.contentRect.width;
			if (newWidth !== previousWidth) {
				previousWidth = newWidth;
				debounce(this, this.updateStretchableFragmentHeight, 200);
			}
		});

		resizeObserver.observe(elementToObserve);
		return resizeObserver;
	}

	// ------ END NO-RERENDER LOGIC	---------

	// check if we should trigger report created event when user adds the third fragment after the initial image and title fragments
	private shouldTriggerReportCreatedEvent(): boolean {
		const report = this.args.report;

		const slides = report.slides;
		if (slides.length !== 1) {
			return false;
		}

		const slide = slides[0]!;
		const fragments = slide.fragments;

		// case 1: TypeCoverPageless slide - trigger on third fragment
		if (slide.isTypeCoverPageless) {
			return fragments.length === 2;
		}

		// case 2: empty slide - trigger on first fragment
		return fragments.length === 0;
	}

	private addFragmentFromLayout(
		fragmentType: NewReportSlideFragmentLayout,
		rowIndex: number,
		columnIndex: number | null,
	) {
		const addData: ReportFragmentActionAddData = {
			fragmentType,
			rowIndex,
			columnIndex,
		};
		const slide =
			this.args.reportSlide || this.args.report.getSlideWithGlobalRowIndex(rowIndex)!;
		const shouldTriggerEvent = this.shouldTriggerReportCreatedEvent();

		this.mmReportSlideFragmentsService
			.addFragmentFromLayout(fragmentType, slide)
			.then(({ fragment, afterFragmentCreateFn }: CreateFragmentResponseToGrid) => {
				fragment.isNewlyCreated = true;
				void this.onFragmentChange(fragment, ReportFragmentAction.ADD, addData).then(() => {
					const promise = afterFragmentCreateFn
						? afterFragmentCreateFn()
						: Promise.resolve();
					void promise.then(() => {
						this.affectedRows.push(fragment[this.positionProperty].row);

						this.updateGridItemColumnSpan();
						this.updateStretchableFragmentHeight();

						if (shouldTriggerEvent) {
							const origin =
								this.mmReportsService.getAndClearReportCreationOrigin() || 'page';
							this.analyticsService.event(TrackingEvent.REPORT_CREATED, {
								origin,
							});
						}
					});
				});
			})
			.catch(() => {
				// do nothing, user probably closed popup
			});
	}

	@action
	async addRow(fragmentType: NewReportSlideFragmentLayout, rowIndex: number) {
		// the row/column given is either position (slide mode) or globalPosition (pageless mode)
		// the transformation will be handled in editor components

		// ensure slide exists
		if (!this.args.report.slides.length) {
			const slide = this.mmReportSlidesService.createNewModel({
				reportId: this.args.report.numericId!,
			});
			await slide.saveAndCatch();
		}

		this.addFragmentFromLayout(fragmentType, rowIndex, null);
	}

	@action
	addFragmentInPosition(
		fragmentType: NewReportSlideFragmentLayout,
		rowIndex: number,
		columnIndex: number,
	): void {
		// the row/column given is either position (slide mode) or globalPosition (pageless mode)
		// the transformation will be handled in editor components
		this.addFragmentFromLayout(fragmentType, rowIndex, columnIndex);
	}

	// ----- resizing stuff -----

	@action
	updateFragmentSize(updates: SettingsSizeData) {
		if (!this.args.isEditable) {
			return;
		}

		return this.requestService
			.api('PUT', `/reports/${this.args.report.id}/report-slide-fragments-settings`, {
				reportSlideFragments: updates,
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	updateGridItemColumnSpan(): void {
		const usePredefinedWidthsOnRows = this.affectedRows;

		// Select all grid items and group them by row
		const gridItems = document.querySelectorAll<HTMLElement>('.report_ct .grid .grid-item');
		const rows: Record<string, HTMLElement[]> = {};

		// Group items by their `data-row` attribute
		gridItems.forEach((item) => {
			const row = +item.getAttribute('data-row')!;

			if (!rows[row]) {
				rows[row] = [];
			}

			rows[row].push(item);
		});

		// Set the grid-column span based on the number of items in each row
		const reportSlideFragments: SettingsSizeData = [];
		Object.entries(rows).forEach(([row, items]) => {
			const itemCount = items.length;

			items.forEach((item: HTMLElement) => {
				const fragment = this.args.model.find(
					(f) => f.id === item.getAttribute('data-id'),
				)!;

				// dont set width for boards to allow edge to edge on pageless
				if (fragment?.isTypeBoard) {
					return;
				}

				let width = fragment?.width;

				if (usePredefinedWidthsOnRows.includes(+row) || !width) {
					if (itemCount === 1) {
						width = 100;
					} else if (itemCount === 2) {
						width = 50;
					} else if (itemCount === 3) {
						width = 33.33;
					}
				}

				item.style.width = `${width}%`;

				if (fragment?.id && fragment?.width !== width) {
					reportSlideFragments.push({
						id: fragment.id,
						settings: { width },
					});

					checkToApplyWatchHack('reports/fragment-grid:directPropUpdate', () => {
						fragment.directPropUpdate({ width });
					});
				}
			});
		});

		if (reportSlideFragments.length) {
			void this.updateFragmentSize(reportSlideFragments);
		}

		this.affectedRows = [];
	}

	// ----- block/image stretchable stuff -----

	@action
	updateStretchableFragmentHeight() {
		const node = this.node;
		if (!node) {
			return;
		}

		if (!isAlive(this)) {
			return;
		}

		const items = Array.from(node.querySelectorAll<HTMLElement>('.grid-item'));

		if (!items.some((i) => i.classList.contains('stretchable'))) {
			return;
		}

		let contentHeight: number;
		let itemsToProcess: HTMLElement[][];

		if (this.isPageless) {
			const wrapper = node.closest<HTMLElement>('.pageless-content')!;
			if (!wrapper) {
				return;
			}

			// calculate available width (16:9 ratio)
			const availableWidth = wrapper.offsetWidth;
			contentHeight = (availableWidth * 9) / 16;

			const itemsByRows: Record<string, HTMLElement[]> = {};
			items.forEach((item) => {
				const row = item.getAttribute('data-row');
				if (!row) return;

				if (!itemsByRows[row]) {
					itemsByRows[row] = [];
				}
				itemsByRows[row].push(item);
			});

			// only process rows that contain stretchable fragments
			itemsToProcess = Object.values(itemsByRows).filter((rowItems) =>
				rowItems.some((item) => {
					const fragment = this.args.model.find(
						(f) => f.id === item.getAttribute('data-id'),
					);
					return fragment?.isStretchableOnSlide;
				}),
			);
		} else {
			contentHeight = node.closest<HTMLElement>('.usercontent')?.offsetHeight || 0;
			itemsToProcess = [items];
		}

		itemsToProcess.forEach((groupItems) => {
			const itemsByRows: HTMLElement[][] = [];
			groupItems.forEach((item: HTMLElement) => {
				const row = +item.getAttribute('data-row')!;
				if (!itemsByRows[row]) {
					itemsByRows[row] = [];
				}
				itemsByRows[row].push(item);
			});

			const itemsByStretchableRows: HTMLElement[][] = [],
				itemsByNormalRows: HTMLElement[][] = [];

			itemsByRows.forEach((rowItems) => {
				if (rowItems.some((i) => this.isElementStretchable(i))) {
					itemsByStretchableRows.push(rowItems);
				} else {
					itemsByNormalRows.push(rowItems);
				}
			});

			// in pageless mode, each stretchable row gets the full contentHeight, otherwise calculate proportional height
			let stretchableHeight: number;
			if (this.isPageless) {
				stretchableHeight = contentHeight;
			} else {
				let freeSpaceForStretchableRows = contentHeight;

				// get free space for stretchable rows by removing each row's height from content height
				itemsByNormalRows.forEach((rowItems) => {
					const rowMaxHeight = this.getMaxHeightFromNonStretchableItems(rowItems);
					freeSpaceForStretchableRows = freeSpaceForStretchableRows - rowMaxHeight;
				});

				// if we have multiple rows with stretchable items, we will stretch them proportionally
				const stretchableRowProportionalHeight =
					freeSpaceForStretchableRows / Math.max(itemsByStretchableRows.length, 1);

				stretchableHeight = stretchableRowProportionalHeight;
			}

			const minStretchableHeight = contentHeight / 3;

			itemsByStretchableRows.forEach((rowItems) => {
				let finalHeight = 0;

				// calculate height from stretchable items
				rowItems.forEach((item) => {
					if (this.isElementStretchable(item)) {
						const fragment = this.args.model.find(
								(f) => f.id === item.getAttribute('data-id'),
							),
							heightPercentage = fragment?.height || 100,
							// calculate height based on stretchableHeight and user's multiplier
							heightWithMultiplier = stretchableHeight * (heightPercentage / 100),
							// ensure it never goes below minStretchableHeight, regardless of user's multiplier
							calculatedHeight = Math.max(minStretchableHeight, heightWithMultiplier);

						finalHeight = Math.max(finalHeight, calculatedHeight);
					}
				});

				// apply height to all items in the row
				rowItems.forEach((item) => {
					item.style.height = `${finalHeight}px`;
				});
			});
		});
	}

	private isElementStretchable(item: HTMLElement) {
		return item.classList.contains('stretchable');
	}

	private getMaxHeightFromNonStretchableItems(items: HTMLElement[]) {
		let maxHeight = 0;
		items.forEach((item) => {
			if (!this.isElementStretchable(item)) {
				// get height of non stretchable items on pageless
				const height = item.offsetHeight;
				if (height > maxHeight) {
					maxHeight = height;
				}
			}
		});
		return maxHeight;
	}

	// ----- sortable stuff ------

	private getOrder(el: JQuery<HTMLElement>): OrdersData {
		const orders: OrdersData = [];
		const items = Array.from(el.find('.grid-item'));

		// Process items sequentially to maintain proper ordering
		let row = 0,
			column = 0;

		for (let index = 0; index < items.length; index++) {
			const item = items[index]!;

			// to drag and drop before first row
			if (
				item.classList.contains('grid-item') &&
				!item.classList.contains('grid-row') &&
				index === 0
			) {
				row = 1;
				column = 1;
			}

			if (item.classList.contains('grid-row')) {
				column = 0;

				// In case last item in row is dragged outside of the row
				if (!item.nextElementSibling?.classList.contains('grid-row')) {
					row++;
				}
				continue;
			}

			if (item.classList.contains('grid-item') && index !== 0) {
				column++;
			}

			const id = item.getAttribute('data-id');
			const newOrder = getOrderFromPosition(row, column); // Generate new order number (11, 12, 13, 21, 22, etc.)

			if (id) {
				orders.push({
					id,
					order: newOrder,
				});

				// Update data attributes to reflect new position
				item.setAttribute('data-row', row.toString());
				item.setAttribute('data-column', column.toString());
				item.setAttribute('data-order', newOrder.toString());
			}
		}

		return orders;
	}

	private updateSortOrder(orders: OrdersData, draggedFragmentId: number): void {
		// the orders given here are relative to the shown fragments
		// so this is either order (slide mode) or globalOrder (pageless mode)
		// the transformation will be handled in editor components
		if (!this.args.isEditable) {
			return;
		}
		const draggedFragment = this.model.find((item) => item.id === String(draggedFragmentId))!;
		void this.onFragmentChange(draggedFragment, ReportFragmentAction.ORDER, {
			orders,
		});
	}

	private checkIfItemCanBeDropped(
		placeholderNextNode: HTMLElement | null,
		placeholderPrevNode: HTMLElement | null,
		wantedRow: number,
		currentRow: number,
	) {
		if (
			((placeholderPrevNode?.classList.contains('grid-row') &&
				placeholderNextNode?.hasAttribute('data-no-drop-from-other-row')) ||
				(placeholderNextNode?.classList.contains('grid-row') &&
					placeholderPrevNode?.hasAttribute('data-no-drop-from-other-row')) ||
				(placeholderNextNode?.hasAttribute('data-no-drop-from-other-row') &&
					placeholderPrevNode?.hasAttribute('data-no-drop-from-other-row'))) &&
			wantedRow !== currentRow
		) {
			return false;
		}

		return true;
	}

	private setupSortable(node: HTMLElement): void {
		if (!this.args.isEditable) {
			return;
		}

		const el = $(node);
		let placeholderNextNode: HTMLElement | null = null,
			placeholderPrevNode: HTMLElement | null = null,
			helperCloneNode: HTMLElement | null = null,
			wantedRow: number,
			currentRow: number,
			cancelSorting = false,
			draggedFragmentId: number | null,
			insertBefore = false;

		el.sortable({
			items: '.grid-item',
			handle: '.handle-vertical',
			tolerance: 'pointer',
			placeholder: 'sort-placeholder',
			start: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				ui.helper.addClass('sort-helper active-helper');

				let currentRow = 0;
				let itemsInCurrentRow = 0;
				const items = Array.from(el.find('.grid-item'));

				const gridRowStart = document.createElement('div');
				gridRowStart.className = 'grid-item grid-row';

				draggedFragmentId = ui.helper.data('id') as number;

				items.forEach((item) => {
					const itemRow = +item.getAttribute('data-row')!;

					if (itemRow !== currentRow) {
						itemsInCurrentRow = el.find(`.grid-item[data-row="${itemRow}"]`).length;
						currentRow = itemRow;

						// Insert the new div before the grid-item
						gridRowStart.setAttribute('data-row', itemRow.toString());
						item.parentNode?.insertBefore(gridRowStart.cloneNode(true), item);

						// Add always place for sorting placeholder on first grid-item in row
						const itemWidth = item.style.width;
						item.setAttribute('data-width', itemWidth);
						item.style.width = `calc(${itemWidth} - ${SORTING_PLACEHOLDER_WIDTH}`;
					}

					if (itemsInCurrentRow === 3) {
						item.setAttribute('data-no-drop-from-other-row', '');
					}
				});

				// Find the container and append the new div
				const container = items[0]?.parentNode;
				if (container) {
					container.appendChild(gridRowStart.cloneNode(true));
				}

				// After adding new divs, refresh the sortable
				el.sortable('refresh');

				// Create helper clone node (to show dragged item on original position)
				const helperNode = ui.helper[0]!;
				helperCloneNode = helperNode.cloneNode(true) as HTMLElement;
				helperCloneNode.classList.remove('sort-helper');
				helperCloneNode.classList.add('helper-clone');
				helperCloneNode.style.position = 'relative';
				helperNode.parentNode?.insertBefore(helperCloneNode, helperNode);
			},
			sort: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				insertBefore = false;

				const placeholderNode = ui.placeholder[0]!;

				placeholderNextNode = placeholderNode.nextElementSibling as HTMLElement | null;
				placeholderPrevNode = placeholderNode.previousElementSibling as HTMLElement | null;

				const insertToTheBeginning =
					placeholderNextNode?.classList.contains('grid-row') &&
					!placeholderPrevNode?.classList.contains('grid-item');

				const insertToTheEnd =
					placeholderPrevNode?.classList.contains('grid-row') &&
					!placeholderNextNode?.classList.contains('grid-item');

				if (placeholderNextNode && insertToTheBeginning) {
					wantedRow = +placeholderNextNode.getAttribute('data-row')!;
					insertBefore = true;
				} else if (placeholderPrevNode && insertToTheEnd) {
					wantedRow = +placeholderPrevNode.getAttribute('data-row')! + 1;
				} else {
					if (placeholderNextNode?.hasAttribute('data-row')) {
						wantedRow = +placeholderNextNode.getAttribute('data-row')!;
					} else if (placeholderPrevNode?.hasAttribute('data-row')) {
						wantedRow = +placeholderPrevNode.getAttribute('data-row')!;
					}
				}

				currentRow = +ui.item[0]!.getAttribute('data-row')!;
				// add check if there is still any item left in current row
				const itemsInCurrentRow = el.find(
					`.grid-item:not(.grid-row):not(.helper-clone)[data-row="${currentRow}"]`,
				).length;
				if (itemsInCurrentRow === 1) {
					if (wantedRow > currentRow) {
						wantedRow--;
					}

					currentRow = wantedRow;
				} else {
					if (insertBefore) {
						currentRow++;
					}
				}

				if (
					!this.checkIfItemCanBeDropped(
						placeholderNextNode,
						placeholderPrevNode,
						wantedRow,
						currentRow,
					)
				) {
					ui.placeholder.css({
						display: 'none',
					});
				} else if (insertToTheBeginning || insertToTheEnd) {
					ui.placeholder.css({
						display: 'block',
						width: '100%',
						height: SORTING_PLACEHOLDER_WIDTH,
					});
				} else {
					ui.placeholder.css({
						display: 'block',
						width: SORTING_PLACEHOLDER_WIDTH,
						height: ui.item.outerHeight()!,
					});
				}
			},
			beforeStop: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				ui.helper.removeClass('sort-helper active-helper');

				// Remove helper clone node
				if (helperCloneNode) {
					helperCloneNode.remove();
					helperCloneNode = null;
				}

				if (
					!this.checkIfItemCanBeDropped(
						placeholderNextNode,
						placeholderPrevNode,
						wantedRow,
						currentRow,
					)
				) {
					cancelSorting = true;

					next(() => {
						el.sortable('cancel'); // prevent droping from other rows if row is full
					});
				}

				el.find('.grid-item[data-no-drop-from-other-row]').each((_index, item) => {
					item.removeAttribute('data-no-drop-from-other-row');
				});

				// Cleanup data-width and set fragment width back to initial (remove place for placeholder)
				el.find('.grid-item[data-width]').each((_index, item) => {
					const width = parseFloat(item.getAttribute('data-width')!);

					item.style.width = `${width}%`;
					item.removeAttribute('data-width');
				});
			},
			update: (_event: JQuery.Event, _ui: jQueryUIInstance) => {
				if (!cancelSorting) {
					const order = this.getOrder(el);
					this.updateSortOrder(order, draggedFragmentId!);

					this.affectedRows = uniq([wantedRow, currentRow]);
					next(() => {
						this.updateGridItemColumnSpan();
					});
					this.updateStretchableFragmentHeight();
				}
			},
			stop: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				const item = ui.item[0]!;
				const items = Array.from(el.find('.grid-row'));

				items.forEach((item) => {
					item.remove();
				});

				const reportSlideFragment = this.mmReportSlideFragmentsService.findModel(
					item.getAttribute('data-id')!,
				);

				if (
					reportSlideFragment &&
					reportSlideFragment.isTypeImage &&
					!reportSlideFragment.isImageShown
				) {
					// Trigger to rerender image upload drop component (https://app.asana.com/0/1138028810573038/1208998069993589/f)
					this.eventManagerService.trigger(AppEvent.RERENDER_IMAGE_UPLOAD_DROP_COMPONENT);
				}

				draggedFragmentId = null;

				cancelSorting = false;
			},
		});
	}

	private destroySortable(node: HTMLElement): void {
		try {
			$(node).sortable('destroy');
		} catch (_e) {
			// skip
		}
	}
	<template>
		<div
			class="grid"
			{{didInsert this.setupView}}
			{{didUpdate this.onModelChange @model}}
			{{didUpdate this.checkModelOrder this.reportSlideFragmentsOrder}}
			{{willDestroy this.teardownView}}
		>
			{{yield
				this.noRerenderSortable.renderedModel
				(component
					this.itemComponent
					isPageless=this.isPageless
					isEditable=@isEditable
					orderProperty=@orderProperty
					slideBackgroundColor=this.slideBackgroundColor
					removeFragment=this.removeFragment
					addFragmentInPosition=this.addFragmentInPosition
					updateFragmentSize=this.updateFragmentSize
					addRow=this.addRow
				)
				this.onFragmentChange
			}}
		</div>

		{{#if this.showAddRow}}
			<ReportsFragmentGridAddRowComponent
				@index={{this.lastRowIndex}}
				@isVisible={{@isEditable}}
				@addRow={{this.addRow}}
				@isPageless={{this.isPageless}}
				@backgroundColor={{this.slideBackgroundColor}}
				@showAddRowPlaceholder={{this.showAddRowPlaceholder}}
			/>
		{{/if}}
	</template>
}
